import { useEffect, useState } from 'react'
import { getDatabase, ref, set, push, onValue, remove, get } from 'firebase/database'
import { createEmptyCustomer, serializeCustomer, deserializeCustomer } from '../models/customer'

const useCustomerDatabase = (id) => {
  const [customer, setCustomer] = useState()
  const [customers, setCustomers] = useState([])
  const db = getDatabase()

  // Listen to a single customer record when id is provided
  useEffect(() => {
    if (!id || id === 'new') {
      setCustomer(undefined)
      return
    }
    const customerRef = ref(db, `Customers/${id}`)
    const unsubscribe = onValue(customerRef, (snapshot) => {
      const val = snapshot.val()
      setCustomer(val ? deserializeCustomer(val) : undefined)
    })
    return () => unsubscribe()
  }, [id, db])

  // Listen to all customers
  useEffect(() => {
    const customersRef = ref(db, 'Customers')
    const unsubscribe = onValue(customersRef, (snapshot) => {
      const val = snapshot.val()
      if (!val) {
        setCustomers([])
        return
      }
      const list = Object.entries(val).map(([key, data]) => ({
        id: key,
        ...deserializeCustomer(data)
      }))
      setCustomers(list)
    })
    return () => unsubscribe()
  }, [db])

  const createCustomer = async (name) => {
    const record = createEmptyCustomer(name)
    return await push(ref(db, 'Customers'), serializeCustomer(record))
  }

  const updateCustomer = async (customerId, data) => {
    return await set(ref(db, `Customers/${customerId}`), serializeCustomer(data))
  }

  const removeCustomer = async (customerId) => {
    return await remove(ref(db, `Customers/${customerId}`))
  }

  const addEmail = async (customerId, email) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    if (!current.emails.includes(email)) {
      current.emails.push(email)
      await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
    }
  }

  const removeEmail = async (customerId, email) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.emails = current.emails.filter((e) => e !== email)
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  const addApiKeyId = async (customerId, keyId) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    if (!current.apiKeyIds.includes(keyId)) {
      current.apiKeyIds.push(keyId)
      await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
    }
  }

  const removeApiKeyId = async (customerId, keyId) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.apiKeyIds = current.apiKeyIds.filter((k) => k !== keyId)
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  const setAccessToken = async (customerId, tokenRef) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.accessToken = tokenRef
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  const setIsMediproCustomer = async (customerId, enabled) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.isMediproCustomer = enabled
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  const setIsAcaproCustomer = async (customerId, enabled) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.isAcaproCustomer = enabled
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  const setSupportPortalLink = async (customerId, link) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.supportPortalLink = link || null
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  const setSupportTicketsEnabled = async (customerId, enabled) => {
    const snap = await get(ref(db, `Customers/${customerId}`))
    const current = deserializeCustomer(snap.val())
    current.supportTicketsEnabled = enabled
    await set(ref(db, `Customers/${customerId}`), serializeCustomer(current))
  }

  return {
    customer,
    customers,
    createCustomer,
    updateCustomer,
    removeCustomer,
    addEmail,
    removeEmail,
    addApiKeyId,
    removeApiKeyId,
    setAccessToken,
    setIsMediproCustomer,
    setIsAcaproCustomer,
    setSupportPortalLink,
    setSupportTicketsEnabled
  }
}

export default useCustomerDatabase
